import { formatDateTimeBR, renderPaymentTypes } from "../../../../../../helpers/format";

export interface IEstornoFechamento {
  id_operacao_patio_pagamento: number;
  id_operacao_patio: number;
  tipo_pagamento: number;
  data_hora_pagamento: string;
  valor_estornado: number;
  numero_transacao?: string | null;
  justificativa_estorno?: string | null;
  usuario_estorno?: string | null;
}

export interface IResumoFechamento {
  valor_abertura: number;
  total_faturado: number;
  total_cartao_credito: number;
  total_cartao_debito: number;
  total_cartao_manual_credito: number;
  total_cartao_manual_debito: number;
  total_pix: number;
  total_dinheiro: number;
  total_pagamentos_bruto: number;
  total_descontos: number;
  total_pagamentos: number;
  total_sangrias: number;
  total_estornado: number;
  total_entradas: number;
  total_saidas: number;
  valor_caixa: number;
  estornos: IEstornoFechamento[];
}

interface IResumoMovimentacao {
  id_operacao_patio_guiche_movimentacao?: number;
  abertura_data_hora?: string;
  abertura_valor_caixa?: number;
  usuario?: string;
}

interface IFechamentoInfo {
  dataFechamento: string;
  valorFechamento: number;
  justificativa: string;
}

interface IFechamentoPdfPayload {
  movimentacao?: IResumoMovimentacao;
  resumo: IResumoFechamento;
  fechamento: IFechamentoInfo;
}

const formatCurrency = (value: number | string | null | undefined) => {
  const numericValue = Number(value ?? 0);

  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(Number.isNaN(numericValue) ? 0 : numericValue);
};

const formatDate = (value?: string | null) => {
  if (!value) {
    return "---";
  }

  try {
    return formatDateTimeBR(value);
  } catch {
    return value;
  }
};

const escapeHtml = (value: string) => value
  .replaceAll("&", "&amp;")
  .replaceAll("<", "&lt;")
  .replaceAll(">", "&gt;")
  .replaceAll('"', "&quot;")
  .replaceAll("'", "&#39;");

const createResumoRows = (resumo: IResumoFechamento, fechamento: IFechamentoInfo) => ([
  ["Valor na abertura", formatCurrency(resumo.valor_abertura)],
  ["Dinheiro", formatCurrency(resumo.total_dinheiro)],
  ["PIX", formatCurrency(resumo.total_pix)],
  ["Cartao de credito", formatCurrency(resumo.total_cartao_credito)],
  ["Cartao de debito", formatCurrency(resumo.total_cartao_debito)],
  ["Cartao manual credito", formatCurrency(resumo.total_cartao_manual_credito)],
  ["Cartao manual debito", formatCurrency(resumo.total_cartao_manual_debito)],
  ["Faturado", formatCurrency(resumo.total_faturado)],
  ["Descontos", formatCurrency(resumo.total_descontos)],
  ["Sangrias", formatCurrency(resumo.total_sangrias)],
  ["Total estornado", formatCurrency(resumo.total_estornado)],
  ["Total entradas", formatCurrency(resumo.total_entradas)],
  ["Total saidas", formatCurrency(resumo.total_saidas)],
  ["Valor no caixa", formatCurrency(resumo.valor_caixa)],
  ["Valor de fechamento informado", formatCurrency(fechamento.valorFechamento)],
  ["Justificativa do fechamento", fechamento.justificativa?.trim() || "---"],
]);

const createEstornosRows = (resumo: IResumoFechamento) => {
  if (resumo.estornos.length === 0) {
    return `
      <tr>
        <td colspan="5" class="empty-state">Nenhum estorno registrado nesta movimentacao.</td>
      </tr>
    `;
  }

  return resumo.estornos.map((estorno) => {
    const pagamentoDescricao = renderPaymentTypes(estorno.tipo_pagamento);
    const numeroTransacao = estorno.numero_transacao ? ` / ${estorno.numero_transacao}` : "";

    return `
      <tr>
        <td>${escapeHtml(formatDate(estorno.data_hora_pagamento))}</td>
        <td>${escapeHtml(String(estorno.id_operacao_patio ?? "---"))}</td>
        <td>${escapeHtml(`${pagamentoDescricao}${numeroTransacao}`)}</td>
        <td>${escapeHtml(formatCurrency(estorno.valor_estornado))}</td>
        <td>${escapeHtml(estorno.justificativa_estorno?.trim() || "---")}</td>
      </tr>
    `;
  }).join("");
};

const createPrintHtml = ({ movimentacao, resumo, fechamento }: IFechamentoPdfPayload) => {
  const resumoRows = createResumoRows(resumo, fechamento)
    .map(([label, value]) => `
      <tr>
        <td>${escapeHtml(label)}</td>
        <td>${escapeHtml(value)}</td>
      </tr>
    `)
    .join("");

  return `<!DOCTYPE html>
    <html lang="pt-BR">
      <head>
        <meta charset="UTF-8" />
        <title>Fechamento de Caixa</title>
        <style>
          :root {
            color-scheme: light;
          }

          * {
            box-sizing: border-box;
          }

          body {
            margin: 0;
            padding: 24px;
            font-family: Arial, Helvetica, sans-serif;
            color: #1f2937;
            background: #ffffff;
          }

          .page {
            max-width: 1024px;
            margin: 0 auto;
          }

          .header {
            display: flex;
            justify-content: space-between;
            gap: 24px;
            margin-bottom: 24px;
          }

          .title {
            font-size: 28px;
            font-weight: 700;
            color: #062d4e;
            margin-bottom: 8px;
          }

          .subtitle {
            font-size: 14px;
            color: #6b7280;
            line-height: 1.5;
          }

          .meta {
            display: grid;
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 12px;
            margin-bottom: 24px;
          }

          .meta-card {
            border: 1px solid #d1d5db;
            border-radius: 12px;
            padding: 14px 16px;
            background: #f9fafb;
          }

          .meta-label {
            font-size: 12px;
            font-weight: 700;
            text-transform: uppercase;
            color: #6b7280;
            margin-bottom: 6px;
          }

          .meta-value {
            font-size: 15px;
            font-weight: 600;
            color: #111827;
            word-break: break-word;
          }

          .section {
            margin-bottom: 28px;
          }

          .section-title {
            font-size: 18px;
            font-weight: 700;
            color: #0c4a85;
            margin-bottom: 12px;
          }

          table {
            width: 100%;
            border-collapse: collapse;
            table-layout: fixed;
          }

          th,
          td {
            border: 1px solid #d1d5db;
            padding: 10px 12px;
            text-align: left;
            vertical-align: top;
            word-wrap: break-word;
          }

          th {
            background: #062d4e;
            color: #ffffff;
            font-size: 13px;
          }

          tbody tr:nth-child(even) {
            background: #f9fafb;
          }

          .empty-state {
            text-align: center;
            color: #6b7280;
            padding: 24px 12px;
          }

          .footer {
            margin-top: 24px;
            font-size: 12px;
            color: #6b7280;
          }

          @media print {
            body {
              padding: 12px;
            }

            .page {
              max-width: none;
            }
          }
        </style>
      </head>
      <body>
        <div class="page">
          <div class="header">
            <div>
              <div class="title">Fechamento de Caixa</div>
              <div class="subtitle">Resumo de valores, fechamento informado e lista detalhada de estornos.</div>
            </div>
          </div>

          <div class="meta">
            <div class="meta-card">
              <div class="meta-label">Movimentacao</div>
              <div class="meta-value">${escapeHtml(String(movimentacao?.id_operacao_patio_guiche_movimentacao ?? "---"))}</div>
            </div>
            <div class="meta-card">
              <div class="meta-label">Usuario</div>
              <div class="meta-value">${escapeHtml(movimentacao?.usuario || "---")}</div>
            </div>
            <div class="meta-card">
              <div class="meta-label">Abertura</div>
              <div class="meta-value">${escapeHtml(formatDate(movimentacao?.abertura_data_hora))}</div>
            </div>
            <div class="meta-card">
              <div class="meta-label">Fechamento</div>
              <div class="meta-value">${escapeHtml(fechamento.dataFechamento || "---")}</div>
            </div>
          </div>

          <div class="section">
            <div class="section-title">Resumo</div>
            <table>
              <thead>
                <tr>
                  <th>Campo</th>
                  <th>Valor</th>
                </tr>
              </thead>
              <tbody>
                ${resumoRows}
              </tbody>
            </table>
          </div>

          <div class="section">
            <div class="section-title">Estornos e justificativas</div>
            <table>
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Operacao</th>
                  <th>Pagamento</th>
                  <th>Valor</th>
                  <th>Justificativa</th>
                </tr>
              </thead>
              <tbody>
                ${createEstornosRows(resumo)}
              </tbody>
            </table>
          </div>

          <div class="footer">Gerado em ${escapeHtml(formatDate(new Date().toISOString()))}</div>
        </div>
      </body>
    </html>`;
};

export const generateFechamentoPdf = ({ movimentacao, resumo, fechamento }: IFechamentoPdfPayload) => {
  const html = createPrintHtml({ movimentacao, resumo, fechamento });

  const iframe = globalThis.document.createElement("iframe");
  iframe.setAttribute("aria-hidden", "true");
  iframe.style.position = "fixed";
  iframe.style.right = "100%";
  iframe.style.bottom = "100%";
  iframe.style.width = "210mm";
  iframe.style.height = "297mm";
  iframe.style.border = "0";
  iframe.style.opacity = "0.01";
  iframe.style.pointerEvents = "none";

  const cleanup = () => {
    iframe.remove();
  };

  iframe.onload = () => {
    const frameWindow = iframe.contentWindow;

    if (!frameWindow) {
      cleanup();
      throw new Error("Nao foi possivel carregar o documento para impressao.");
    }

    const onAfterPrint = () => {
      frameWindow.removeEventListener("afterprint", onAfterPrint);
      cleanup();
    };

    frameWindow.addEventListener("afterprint", onAfterPrint);
    globalThis.setTimeout(() => {
      frameWindow.focus();
      frameWindow.print();
    }, 50);

    // Fallback caso o evento afterprint nao dispare no navegador.
    globalThis.setTimeout(cleanup, 60000);
  };

  globalThis.document.body.appendChild(iframe);
  iframe.srcdoc = html;
};